import { z } from 'zod';

import { GuardianInviteSchema } from '../guardian-invite/guardian-invite';
import { GuardianLinkSchema } from '../guardian-link/guardian-link';

/**
 * Everything the ward's guardian screen draws, in one read.
 *
 * **Not paginated, and that is a property of the domain rather than a shortcut.**
 * Both collections are bounded by operator settings — `guardian.maxActiveLinks`
 * and `guardian.maxOpenInvites`, single digits — so a page is a control over a
 * list that can never need one. The bound is enforced at the write, so this read
 * cannot be made large by a client.
 *
 * **Live rows only.** `links` holds standing guardians and `invites` holds codes
 * that can still be redeemed; a revoked guardian and a lapsed code are both
 * absent. The trail of who *was* a guardian is real and it is kept — in the
 * audit log, which is where an investigation looks — but it is not what this
 * screen is for, and drawing a revoked wali beside a live one is how somebody
 * revokes the wrong row.
 *
 * The two maxima ride along because the screen has to say *why* the button is
 * gone. A limit the client cannot see is a control that silently stops working.
 */
export const GuardianStandingSchema = z
  .object({
    /** Standing guardians. Every one of these can read her conversations (P24b). */
    links: z.array(GuardianLinkSchema),
    /** Codes still outstanding — each one a key nobody has used yet. */
    invites: z.array(GuardianInviteSchema),
    /**
     * May she issue another code?
     *
     * Answered by the server rather than left to the client to derive from the
     * counts below, so the button and the route agree by construction: the same
     * service method decides this and refuses the write, and there is no second
     * evaluator to drift (P23b1 — a permission is an ANSWER).
     */
    canInvite: z.boolean(),
    maxActiveLinks: z.number().int().min(0),
    maxOpenInvites: z.number().int().min(0),
  })
  .strict();

export type GuardianStanding = z.infer<typeof GuardianStandingSchema>;
